// Browser-side WebAuthn helpers: encoding, capability detection, register/login flows.

import { api } from "../api/client";
import type { AuthMethodResponse, Me } from "../types";

// --- Encoding ---------------------------------------------------------------

export function b64urlToBytes(value: string): ArrayBuffer {
  const padded = value + "=".repeat((4 - (value.length % 4)) % 4);
  const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
  return buffer;
}

export function bytesToB64url(value: ArrayBuffer | Uint8Array): string {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

// --- Capability -------------------------------------------------------------

export async function passkeyAvailable(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (typeof window.PublicKeyCredential === "undefined") return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// --- Options decoding -------------------------------------------------------
// The server returns WebAuthn JSON with base64url-encoded fields; the browser
// API needs ArrayBuffer values. Convert the parts it cares about.

type PKCredentialCreationOptionsJSON = {
  challenge: string;
  user: { id: string; name: string; displayName: string };
  rp: { id?: string; name: string };
  pubKeyCredParams: { type: "public-key"; alg: number }[];
  excludeCredentials?: { id: string; type: "public-key"; transports?: string[] }[];
  authenticatorSelection?: {
    residentKey?: ResidentKeyRequirement;
    userVerification?: UserVerificationRequirement;
    requireResidentKey?: boolean;
  };
  timeout?: number;
  attestation?: AttestationConveyancePreference;
};

type PKCredentialRequestOptionsJSON = {
  challenge: string;
  rpId?: string;
  allowCredentials?: { id: string; type: "public-key"; transports?: string[] }[];
  userVerification?: UserVerificationRequirement;
  timeout?: number;
};

function toCreationOptions(json: PKCredentialCreationOptionsJSON): PublicKeyCredentialCreationOptions {
  return {
    challenge: b64urlToBytes(json.challenge),
    rp: { id: json.rp.id, name: json.rp.name },
    user: {
      id: b64urlToBytes(json.user.id),
      name: json.user.name,
      displayName: json.user.displayName,
    },
    pubKeyCredParams: json.pubKeyCredParams,
    excludeCredentials: (json.excludeCredentials ?? []).map((c) => ({
      id: b64urlToBytes(c.id),
      type: "public-key",
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
    authenticatorSelection: json.authenticatorSelection,
    timeout: json.timeout,
    attestation: json.attestation,
  };
}

function toRequestOptions(json: PKCredentialRequestOptionsJSON): PublicKeyCredentialRequestOptions {
  return {
    challenge: b64urlToBytes(json.challenge),
    rpId: json.rpId,
    allowCredentials: (json.allowCredentials ?? []).map((c) => ({
      id: b64urlToBytes(c.id),
      type: "public-key",
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
    userVerification: json.userVerification,
    timeout: json.timeout,
  };
}

// --- Serialisation back to the server --------------------------------------

function serialiseAttestation(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: bytesToB64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bytesToB64url(response.attestationObject),
      clientDataJSON: bytesToB64url(response.clientDataJSON),
    },
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
  };
}

function serialiseAssertion(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bytesToB64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bytesToB64url(response.authenticatorData),
      clientDataJSON: bytesToB64url(response.clientDataJSON),
      signature: bytesToB64url(response.signature),
      userHandle: response.userHandle ? bytesToB64url(response.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
  };
}

// --- High-level flows -------------------------------------------------------

export async function registerPasskey(deviceName: string): Promise<void> {
  const { token, options } = await api.post<{
    token: string;
    options: PKCredentialCreationOptionsJSON;
  }>("/api/auth/passkey/register/options");

  const credential = (await navigator.credentials.create({
    publicKey: toCreationOptions(options),
  })) as PublicKeyCredential | null;
  if (!credential) {
    throw new Error("Passkey registration was cancelled.");
  }

  await api.post("/api/auth/passkey/register/verify", {
    token,
    device_name: deviceName,
    credential: serialiseAttestation(credential),
  });
}

export async function loginWithPasskey(identifier: string): Promise<Me> {
  const { token, options } = await api.post<{
    token: string;
    options: PKCredentialRequestOptionsJSON;
  }>("/api/auth/passkey/login/options", { identifier });

  const credential = (await navigator.credentials.get({
    publicKey: toRequestOptions(options),
  })) as PublicKeyCredential | null;
  if (!credential) {
    throw new Error("Passkey sign-in was cancelled.");
  }

  await api.post("/api/auth/passkey/login/verify", {
    token,
    credential: serialiseAssertion(credential),
  });
  return api.get<Me>("/api/auth/me");
}

export async function fetchAuthMethod(identifier: string): Promise<AuthMethodResponse> {
  const params = new URLSearchParams({ identifier });
  return api.get<AuthMethodResponse>(`/api/auth/method?${params.toString()}`);
}

export function guessDeviceName(): string {
  if (typeof navigator === "undefined") return "This Device";
  const ua = navigator.userAgent;
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  if (/Android/i.test(ua)) return "Android Device";
  if (/Mac/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows PC";
  return "This Device";
}
